/**
 * The narration strip — one block per step (a narration audio segment) laid out
 * by duration. This is the only place "steps" surface in the element-major
 * timeline: click a block to select its step (edits its audio in the right rail),
 * drag to reorder the narration, or delete it. Reveals live on their own tracks
 * below and are timed against the continuous narration, not boxed into a block.
 */

import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2 } from 'lucide-react';

import type { RevealStep } from '@/lib/schema.types';
import { cn } from '@/lib/utils';

import type { StepSpan } from './playback';

export const NARRATION_H = 26;

interface Props {
  steps: RevealStep[];
  spans: StepSpan[];
  pxPerMs: number;
  selectedStepId: string | null;
  onSelect: (id: string) => void;
  onReorder: (ids: string[]) => void;
  onDelete: (id: string) => void;
}

export function NarrationTrack({
  steps,
  spans,
  pxPerMs,
  selectedStepId,
  onSelect,
  onReorder,
  onDelete,
}: Props) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={({ active, over }) => {
        if (!over || active.id === over.id) return;
        const oldI = steps.findIndex((s) => s.id === active.id);
        const newI = steps.findIndex((s) => s.id === over.id);
        if (oldI < 0 || newI < 0) return;
        onReorder(arrayMove(steps, oldI, newI).map((s) => s.id));
      }}
    >
      <SortableContext items={steps.map((s) => s.id)} strategy={horizontalListSortingStrategy}>
        <div className="flex" style={{ height: NARRATION_H }}>
          {steps.map((step, i) => (
            <Block
              key={step.id}
              step={step}
              index={i}
              width={Math.max(8, Math.round(spans[i].dur * pxPerMs))}
              selected={step.id === selectedStepId}
              onSelect={() => onSelect(step.id)}
              onDelete={() => onDelete(step.id)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function Block({
  step,
  index,
  width,
  selected,
  onSelect,
  onDelete,
}: {
  step: RevealStep;
  index: number;
  width: number;
  selected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    width,
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      className={cn(
        'group/b flex h-full shrink-0 cursor-pointer items-center gap-1 border-r border-border px-1.5 text-[10px]',
        selected ? 'bg-accent-soft/70 text-accent' : 'bg-bg-subtle/50 text-fg-muted hover:bg-bg-subtle',
      )}
    >
      <span
        {...attributes}
        {...listeners}
        className="cursor-grab text-fg-subtle active:cursor-grabbing"
        title="Drag to reorder narration"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical size={11} />
      </span>
      <span className="font-semibold">{String(index + 1).padStart(2, '0')}</span>
      <span className="truncate font-medium text-fg/70">{step.label || 'Narration'}</span>
      <span className="ml-auto font-mono text-[9px] text-fg-subtle">
        {(spanDur(step) / 1000).toFixed(1)}s
      </span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="hidden text-fg-subtle hover:text-red-500 group-hover/b:block"
        title="Delete narration segment"
      >
        <Trash2 size={11} />
      </button>
    </div>
  );
}

function spanDur(step: RevealStep): number {
  return step.audio?.durationMs ?? 3000;
}
